import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchAlertStatus, acknowledgeAlert } from '../services/localApi';

const POLL_INTERVAL_MS = 2000;
const ACK_SUCCESS_DISPLAY_MS = 1500;

export default function useAlertPolling() {
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [isConnected, setIsConnected] = useState(true);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [ackError, setAckError] = useState(null);
  const [confirmedId, setConfirmedId] = useState(null);
  const intervalRef = useRef(null);
  const confirmedRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const data = await fetchAlertStatus();
      setActiveAlerts(prev => {
        const alerts = data.alerts;
        if (confirmedRef.current != null) {
          // Preserve the confirmed alert from previous state so the overlay
          // stays mounted for the full success display duration.
          // Update all other alerts from the fresh poll data.
          const confirmed = prev.find(a => a.id === confirmedRef.current);
          const rest = alerts.filter(a => a.id !== confirmedRef.current);
          return confirmed ? [confirmed, ...rest] : rest;
        }
        return alerts;
      });
      setIsConnected(true);
    } catch (err) {
      console.error('Alert polling failed:', err);
      setActiveAlerts([]);
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [poll]);

  const acknowledge = useCallback(async (id, iEquip) => {
    setIsAcknowledging(true);
    setAckError(null);
    try {
      const result = await acknowledgeAlert(id, iEquip);
      if (result.success) {
        // Enter success state — keep overlay visible with confirmed feedback
        confirmedRef.current = id;
        setConfirmedId(id);
        setTimeout(() => {
          setActiveAlerts(prev => prev.filter(a => a.id !== id));
          setConfirmedId(null);
          confirmedRef.current = null;
        }, ACK_SUCCESS_DISPLAY_MS);
      } else {
        setAckError(result.message || 'Échec de la confirmation');
      }
    } catch (err) {
      console.error('Acknowledge failed:', err);
      const apiMessage = err.response?.data?.message;
      setAckError(apiMessage
        ? `Erreur de connexion. Veuillez réessayer. (${apiMessage})`
        : 'Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsAcknowledging(false);
    }
  }, []);

  return { activeAlerts, isConnected, acknowledge, isAcknowledging, ackError, confirmedId };
}
